import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { commonSections, ISection, ISectionEntries } from './sectionConstants';
import { createStyles } from 'utils/createStyle';
import { CoordinatorDashboardContext } from './context';
import { useRootSelector } from 'rootStore';
import { ReactComponent as NotificationBell } from 'media/images/notif-bell.svg';
import { Link } from 'react-router-dom';

const [orders, reports, participants, organization, payments, resources, inventory] =
    commonSections;

export const sections = {
    Orders: () => <Section section={orders} />,
    Reports: () => <Section section={reports} />,
    Participants: () => <Section section={participants} />,
    Organization: (props: { isEnabled: (key: string) => boolean }) => (
        <Section section={organization} isEnabled={props.isEnabled} />
    ),
    Payments: () => <Section section={payments} />,
    Resources: () => <Section section={resources} />,
    Inventory: () => <Section section={inventory} />,
};

interface TitleProps {
    secondary?: boolean;
}

const Title = ({ children, secondary }: React.PropsWithChildren<TitleProps>) => (
    <span className={secondary ? 'text-brand-dark text-lg' : 'text-grey-2 text-sm'}>
        {children}
    </span>
);

const Section = ({
    section,
    isEnabled,
}: {
    section: ISection<ISectionEntries>;
    isEnabled?: (key: string) => boolean;
}) => {
    // hides section entries for specific roles
    const roleId = useRootSelector((s) => s.store.account.role);
    const entries = section.entries.filter((entry) => {
        const shouldShow = !entry.hideForRoles || !entry.hideForRoles.includes(roleId);

        if (shouldShow && isEnabled && entry.featureFlags?.length) {
            return entry.featureFlags?.some(isEnabled);
        }

        return shouldShow;
    });

    if (entries.length <= 0) {
        return null;
    }

    const id = section.id ?? '';

    return (
        <div className={`${id} mb-8`}>
            <Title>{section.title}</Title>
            <Entries entries={entries} />
        </div>
    );
};

const Entries = ({ entries }: { entries: ISectionEntries[] }) => {
    const { isScripWare } = useContext(CoordinatorDashboardContext);

    return (
        <div className="pl-2 mt-3.5">
            {entries.map((entry: ISectionEntries, index: number) => {
                return (
                    <React.Fragment key={index}>
                        {!isScripWare || entry.forScripWare ? <Entry entry={entry} /> : null}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

const Entry = ({ entry }: { entry: ISectionEntries }) => {
    const hasNotifications = useSelector(entry?.checkNotifications || (() => false));
    const styles = genStyles();
    const id = entry.id ?? '';

    const Wrapper = ({ children }: React.PropsWithChildren) => {
        if (entry.internalLink) {
            return (
                <Link style={{ textDecoration: 'none' }} to={entry.link}>
                    {children}
                </Link>
            );
        }

        return (
            <a href={entry.link} style={{ textDecoration: 'none' }}>
                {children}
            </a>
        );
    };

    return (
        <div className={`${id} ${styles.entryContainer}`}>
            <Wrapper>
                <div className="flex justify-between">
                    <Title secondary>
                        {entry.title}
                        {hasNotifications && (
                            <NotificationBell className={styles.title} width="15px" />
                        )}
                    </Title>
                    <div className="underline">Link</div>
                </div>
                <div className={styles.entryDescription}>{entry.description}</div>
            </Wrapper>
        </div>
    );
};

const genStyles = () => {
    return createStyles({
        // entry styles
        entryContainer: [
            'py-6 border-b border-grey-1',
            {
                '&:first-child': { paddingTop: 0 },
                '&:last-child': { borderBottom: 0 },
            },
        ],
        entryDescription: ['text-grey-2 text-lg'],
        entryLink: ['flex items-center gap-2 text-xs'],
        title: ['inline ml-2', { verticalAlign: 'sub' }],
    });
};
